import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Eye, EyeOff, RefreshCw, Link as LinkIcon, ExternalLink } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useSitePageVisibility } from "@/context/SitePageVisibilityContext";

const GROUP_TITLES = {
  public: "Public pages",
  landing_sections: "Landing sections",
  legal: "Legal pages",
};

// Landing anchors don't participate in header/footer/direct-URL semantics
// the same way — they only have the "shown on landing page" toggle. We
// hide the footer + active columns for them.
const IS_SECTION = (key) => key.startsWith("section_");

function normalizeEntry(v) {
  if (v === undefined || v === null) return { show_in_header: true, show_in_footer: false, page_active: true };
  if (typeof v === "boolean") return { show_in_header: v, show_in_footer: v, page_active: v };
  return {
    show_in_header: !!v.show_in_header,
    show_in_footer: !!v.show_in_footer,
    page_active: v.page_active !== false,
  };
}

export default function AdminSitePagesPage() {
  const { refresh } = useSitePageVisibility();
  const [pages, setPages] = useState({});
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site-pages/visibility");
      // Normalise every row so downstream reads never worry about legacy
      // bool docs coming in from an older DB.
      const normalized = {};
      Object.entries(data.data.pages || {}).forEach(([k, v]) => { normalized[k] = normalizeEntry(v); });
      setPages(normalized);
      setGroups(data.data.groups || {});
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateField = (key, field) => (val) => {
    setPages((p) => {
      const next = { ...p, [key]: { ...normalizeEntry(p[key]) } };
      next[key][field] = val;
      // Business rule 4: page_active=false auto-clears the surface toggles.
      if (field === "page_active" && val === false) {
        next[key].show_in_header = false;
        next[key].show_in_footer = false;
        toast.warning("Page disabled — removed from header & footer automatically.");
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-pages/visibility", { pages });
      toast.success("Page visibility updated.");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const copyLink = async (href) => {
    // Build the absolute URL against whatever host we're on.
    const url = href.startsWith("http") ? href : `${window.location.origin}${href}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Page link copied successfully.");
    } catch {
      // Older browsers — fall back to a temporary textarea.
      const el = document.createElement("textarea");
      el.value = url; document.body.appendChild(el); el.select();
      document.execCommand("copy"); document.body.removeChild(el);
      toast.success("Page link copied successfully.");
    }
  };

  if (loading) return <DashboardLayout title="Site pages"><LoadingBlock label="Loading…" /></DashboardLayout>;

  const active = Object.values(pages).filter((e) => e.page_active).length;
  const hidden = Object.values(pages).filter((e) => !e.page_active).length;

  return (
    <DashboardLayout title="Site pages" breadcrumbs={["Super Admin", "Settings", "Site pages"]}>
      <div className="max-w-5xl space-y-6" data-testid="admin-site-pages">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="max-w-2xl">
            <h1 className="text-lg font-bold text-slate-900">Website page visibility</h1>
            <p className="mt-1 text-sm text-slate-500">
              Control each page in three independent ways. <b>Show in Header</b> = link visible in top nav ·
              <b> Show in Footer</b> = link visible in footer · <b>Page Active</b> = direct URL opens.
              Turning off just the header keeps the page usable via direct link (unlisted).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
              <Eye className="h-3 w-3" /> {active} active
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              <EyeOff className="h-3 w-3" /> {hidden} disabled
            </span>
            <button type="button" onClick={load} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40" data-testid="site-pages-refresh">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
        </div>

        {Object.entries(groups).map(([groupKey, items]) => (
          <div key={groupKey} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{GROUP_TITLES[groupKey] || groupKey}</div>
            </div>
            <div className="hidden md:grid grid-cols-[minmax(0,1fr)_repeat(3,7rem)_9rem] items-center gap-3 border-b border-slate-100 bg-slate-50 px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              <div>Page</div>
              <div className="text-center">Header</div>
              <div className="text-center">Footer</div>
              <div className="text-center">Active</div>
              <div className="text-center">Actions</div>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map((row) => {
                // row shape: [key, label, path, defaultHeader, defaultFooter]
                const [key, label, href] = row;
                const entry = normalizeEntry(pages[key]);
                const isSection = IS_SECTION(key);
                const statusChip = entry.page_active
                  ? (entry.show_in_header || entry.show_in_footer
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-700">Listed</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-800">Unlisted</span>)
                  : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[9px] font-bold uppercase text-slate-600">Disabled</span>;
                return (
                  <div key={key} className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_repeat(3,7rem)_9rem] items-center gap-3 p-4" data-testid={`site-page-row-${key}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-bold text-slate-800 truncate">{label}</div>
                        {statusChip}
                      </div>
                      <div className="mt-0.5 text-[11px] font-mono text-slate-500 truncate">{href}</div>
                    </div>
                    <div className="flex justify-center md:justify-center gap-2 items-center">
                      <span className="text-[10px] font-bold uppercase text-slate-500 md:hidden">Header</span>
                      <Switch checked={entry.show_in_header && entry.page_active} disabled={!entry.page_active} onCheckedChange={updateField(key, "show_in_header")} data-testid={`site-page-header-${key}`} />
                    </div>
                    <div className="flex justify-center md:justify-center gap-2 items-center">
                      <span className="text-[10px] font-bold uppercase text-slate-500 md:hidden">Footer</span>
                      {isSection ? (
                        <span className="text-[10px] text-slate-400" title="Landing sections don't appear in the footer.">n/a</span>
                      ) : (
                        <Switch checked={entry.show_in_footer && entry.page_active} disabled={!entry.page_active} onCheckedChange={updateField(key, "show_in_footer")} data-testid={`site-page-footer-${key}`} />
                      )}
                    </div>
                    <div className="flex justify-center md:justify-center gap-2 items-center">
                      <span className="text-[10px] font-bold uppercase text-slate-500 md:hidden">Active</span>
                      <Switch checked={entry.page_active} onCheckedChange={updateField(key, "page_active")} data-testid={`site-page-active-${key}`} />
                    </div>
                    <div className="flex items-center justify-center gap-1">
                      <button type="button" onClick={() => copyLink(href)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-[#101876]/40 hover:text-[#101876]" data-testid={`site-page-copy-${key}`} title="Copy page link">
                        <LinkIcon className="h-3 w-3" /> Copy
                      </button>
                      <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-[#101876]/40 hover:text-[#101876]" data-testid={`site-page-preview-${key}`} title="Open in new tab">
                        <ExternalLink className="h-3 w-3" /> Preview
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="sticky bottom-4 flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-[#329112] px-6 py-3 text-sm font-bold text-white shadow-lg hover:bg-[#26720d] disabled:opacity-70"
            data-testid="site-pages-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
